import { BRAND } from "constants/brand";
/**
 * Sliders management page — lists all homepage sliders with CRUD + visibility toggle.
 *
 * Wires together:
 * - useSliders hook for data fetching and mutations
 * - useGetVendorsDropdown for the vendor dropdown in Add/Edit forms (lightweight id/name only)
 * - useCity for the city dropdown in Add/Edit forms (geo-targeting)
 *
 * The table shows two new columns vs. the original:
 * - **المدينة (City)**: Which city the slider is targeted to, or "جميع المدن" for global.
 * - **الحالة (Status)**: Inline toggle button — click to flip isHidden. Hidden sliders render
 *   at 50% opacity so admins can visually distinguish them.
 */
import { Ring } from "@uiball/loaders";
import "assets/styles/table.scss";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { Toast } from "primereact/toast";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import { useGetVendorsDropdown } from "hooks/useVendors";
import useSliders from "hooks/useSliders";
import useCity from "hooks/useCity";
import Slider from "./Slider/Slider";
import AddSlider from "./AddSlider/Addslider";
import EditSlider from "./EditSlider/EditSlider";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import { Form } from "react-bootstrap";
import { moveSlider, slidersOfCity, slidersOfStatus, SLIDER_STATUS } from "utils/sliderOrder";
import "./sliderOrder.scss";

const COLUMNS = [
    { id: "id", label: "#", width: "10px" },
    // The place on the customer's home screen, beside the identity: it is
    // what the row is, and the rows are in this order.
    {
        id: "order",
        label: "الترتيب",
        hint: "ترتيب الصور في الصفحة الرئيسية للتطبيق. الأولى هي التي تظهر أولًا. اختر مدينة لترى ترتيب صورها كما يراه زبونها",
        width: "90px",
    },
    { id: "title", label: "العنوان", width: "100px" },
    { id: "vendor", label: "المطعم", width: "80px" },
    { id: "link", label: "رابط خارجي", width: "100px" },
    { id: "city", label: "المدينة", width: "80px" },
    { id: "status", label: "الحالة", width: "60px" },
    { id: "actions", label: "الاعدادات", width: "50px" },
];

const Sliders = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [selectedSlider, setSelectedSlider] = useState(null);
    const { loading, deleteSlider, fetchSliders, toggleSliderHidden, reorderSliders } =
        useSliders();
    // "" = every slider. A city = what that city's customer sees: its own
    // sliders and the global ones.
    const [cityFilter, setCityFilter] = useState("");
    // The page opens on the shown sliders: the list the customer sees.
    const [statusFilter, setStatusFilter] = useState(SLIDER_STATUS.SHOWN);
    const { vendors } = useGetVendorsDropdown();
    const { cities } = useCity();
    const data = useSelector((state) => state.slider.sliders);
    const toast = useRef(null);
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("sliders", COLUMNS);

    useEffect(() => {
        fetchSliders();
    }, []);

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    const toggleAddForm = () => {
        setShowAddForm((pre) => (pre = !pre));
    };

    const toggleEditForm = () => {
        setShowEditForm((pre) => (pre = !pre));
    };

    const setSelectedSliderValue = (slider) => {
        setSelectedSlider(slider);
    };

    const toggleDelete = () => {
        setShowDelete((pre) => (pre = !pre));
    };

    /** Inline visibility toggle — flips isHidden via the dedicated API endpoint. */
    const handleToggleHidden = async (id) => {
        try {
            const response = await toggleSliderHidden(id);
            if (response) {
                showMessage("success", "تم التعديل", "تم تغيير حالة السلايدر بنجاح");
            } else {
                showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
            }
        } catch (err) {
            console.error("Error toggling slider:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    const visible = slidersOfStatus(slidersOfCity(data, cityFilter), statusFilter);

    /** Moves one slider one step in the list on screen, and saves the order. */
    const handleMove = async (slider, direction) => {
        const nextIds = moveSlider(
            data.map((row) => row.id),
            visible.map((row) => row.id),
            slider.id,
            direction,
        );
        if (!nextIds) return;
        const byId = new Map(data.map((row) => [row.id, row]));
        const result = await reorderSliders(nextIds.map((id) => byId.get(id)));
        if (!result.ok) {
            showMessage("error", "تعذر حفظ الترتيب", result.message || "حدث خطأ غير متوقع");
        }
    };

    const confirmDelete = async () => {
        try {
            const response = await deleteSlider(selectedSlider.id);

            if (response) {
                showMessage("warn", "تم الحذف", "تم حذف سلايدر بنجاح");
                toggleDelete();
            } else {
                showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
            }
        } catch (err) {
            console.error("Error adding vendor:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div className="controls">
                        <div style={{ display: "flex", gap: "10px" }}>
                            <Form.Select
                                value={cityFilter}
                                onChange={(event) => setCityFilter(event.target.value)}
                                title="اعرض صور مدينة واحدة بالترتيب الذي يراه زبونها"
                            >
                                <option value="">المدينة: الكل</option>
                                {cities?.map((city) => (
                                    <option key={city.id} value={city.id}>
                                        {city.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value)}
                                title="اعرض الصور الظاهرة للزبون، أو المخفية، أو الكل"
                            >
                                <option value={SLIDER_STATUS.SHOWN}>الحالة: ظاهر</option>
                                <option value={SLIDER_STATUS.HIDDEN}>الحالة: مخفي</option>
                                <option value={SLIDER_STATUS.ALL}>الحالة: الكل</option>
                            </Form.Select>
                        </div>
                        <div>
                            <button className="button" onClick={toggleAddForm}>
                                إضافه سلايدر
                            </button>
                        </div>
                        <div style={{ marginInlineStart: "auto" }}>
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>
                    <div className="table-container">
                        <table>
                            <TableHead visibleColumns={visibleColumns} />
                            <tbody>
                                {visible.length ? (
                                    visible.map((slider, index) => (
                                        <Slider
                                            key={slider.id}
                                            slider={slider}
                                            place={index + 1}
                                            isFirst={index === 0}
                                            isLast={index === visible.length - 1}
                                            onMove={handleMove}
                                            visibleColumns={visibleColumns}
                                            selectedSlider={selectedSlider}
                                            toggleEditForm={toggleEditForm}
                                            setSelectedSliderValue={setSelectedSliderValue}
                                            toggleDelete={toggleDelete}
                                            onToggleHidden={handleToggleHidden}
                                        />
                                    ))
                                ) : (
                                    <h4>
                                        {data.length
                                            ? "لا توجد صور بهذه الحالة. غيّر «الحالة» أو «المدينة»"
                                            : "لا يوجد بيانات!"}
                                    </h4>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <AddSlider
                        show={showAddForm}
                        close={toggleAddForm}
                        showMessage={showMessage}
                        vendors={vendors}
                        cities={cities}
                    />
                    <EditSlider
                        show={showEditForm}
                        close={toggleEditForm}
                        showMessage={showMessage}
                        slider={selectedSlider}
                        vendors={vendors}
                        cities={cities}
                    />
                    <DeleteItem
                        show={showDelete}
                        close={toggleDelete}
                        loading={loading}
                        confirm={confirmDelete}
                    />
                </>
            )}
        </div>
    );
};
export default Sliders;
